import React, { useState } from 'react';
import { Filter, X } from 'lucide-react';
import Button from '../../../ui/Button';
import ConditionBuilder from '../../../common/ConditionBuilder';
import { Condition } from '../../../../types/segments/segments.types';
import { CONDITION_OPTIONS } from '../../../../constants/segmentConstants';
import { validateConditions } from '../../../../utils/conditionValidation';

interface ConditionsCardProps {
  onClose?: () => void;
  onSave?: (conditionsData: ConditionsData) => void;
  initialConditions?: { [key: string]: Condition[] };
  onConditionsChange?: (conditions: { [key: string]: Condition[] }) => void;
}

interface ConditionsData {
  conditions: { [key: string]: Condition[] };
}

const ConditionsCard: React.FC<ConditionsCardProps> = ({
  onClose,
  onSave,
  initialConditions,
  onConditionsChange,
}) => {
  const [conditions, setConditions] = useState<{ [key: string]: Condition[] }>(
    initialConditions || { and: [] }
  );

  const handleConditionsChange = (newConditions: {
    [key: string]: Condition[];
  }) => {
    setConditions(newConditions);
    if (onConditionsChange) {
      onConditionsChange(newConditions);
    }
  };

  const handleSave = () => {
    const validation = validateConditions(conditions);

    if (validation.isValid) {
      onSave?.({ conditions });
      onClose?.();
    }
  };

  // Get validation result
  const validation = validateConditions(conditions);

  return (
    <div className={`block`}>
      <div className="bg-card max-w-md rounded-l-lg shadow-md border-r-2 border-border h-[calc(55vh-100px)] overflow-y-auto z-50 border border-border">
        {/* Header */}
        <div className="flex items-center justify-between p-4">
          <div className="flex items-center space-x-2">
            <div className="w-6 h-6 bg-success-background rounded-lg flex items-center justify-center">
              <Filter className="w-4 h-4 text-success" />
            </div>
            <div>
              <h2 className="text-base font-semibold text-gray-900">
                Conditions
              </h2>
              <p className="text-xs text-gray-600">
                Check whether or not people meet specific conditions before they
                enter the delay or perform the action.
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-4">
          {/* Condition Builder */}
          <ConditionBuilder
            conditions={conditions}
            onConditionsChange={handleConditionsChange}
            fieldErrors={validation.fieldErrors}
            dataSourceOptions={CONDITION_OPTIONS}
          />
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end p-4 gap-2">
          <Button
            variant="outline"
            onClick={onClose}
            size="small"
            className="text-sm"
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            disabled={!validation.isValid}
            onClick={handleSave}
            size="small"
            className="text-sm disabled:bg-gray-300 disabled:text-gray-500"
          >
            Save changes
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ConditionsCard;
